/**
 * Shared helpers for domain status SSOTs (Study/Site/Milestone/Visit pattern).
 */

export const canonicalizeStatusKey = (value: string): string =>
  value.toLowerCase().replace(/[\s/\-_]+/g, "");

export interface StatusHelpers<T extends string> {
  VALUES: readonly T[];
  tryNormalize: (status: string) => T | null;
  normalize: (status: string) => T;
  isStatus: (status: string, target: T) => boolean;
  isAnyStatus: (status: string, ...targets: T[]) => boolean;
}

/**
 * Build normalize / is* helpers from a const status map.
 * Extra aliases (e.g. Title Case ↔ SCREAMING_SNAKE) merge into the lookup table.
 */
export function createStatusHelpers<T extends string>(
  statusMap: Record<string, T>,
  defaultValue: T,
  extraAliases: Record<string, T> = {},
): StatusHelpers<T> {
  const VALUES: readonly T[] = Object.values(statusMap);
  const byKey: Record<string, T> = {};

  for (const value of VALUES) {
    byKey[canonicalizeStatusKey(value)] = value;
  }
  for (const [alias, value] of Object.entries(extraAliases)) {
    byKey[canonicalizeStatusKey(alias)] = value;
  }

  const tryNormalize = (status: string): T | null => {
    if (!status?.trim()) {
      return null;
    }
    if (VALUES.includes(status as T)) {
      return status as T;
    }
    return byKey[canonicalizeStatusKey(status)] ?? null;
  };

  const normalize = (status: string): T => tryNormalize(status) ?? defaultValue;

  /** Strict compare: unknown / empty status never matches (no default fallback). */
  const isStatus = (status: string, target: T): boolean =>
    tryNormalize(status) === target;

  const isAnyStatus = (status: string, ...targets: T[]): boolean => {
    const normalized = tryNormalize(status);
    return normalized !== null && targets.includes(normalized);
  };

  return { VALUES, tryNormalize, normalize, isStatus, isAnyStatus };
}
